import { families, PAGE_SIZE, regions, type FamilySlug, type RegionSlug } from "@/config/catalog";
import { searchKey } from "@/lib/utils/text";
import { lowestPrice } from "./mappers/search";
import { CODE_MAX_LENGTH, codeKey, matchesCode } from "./tour-code";
import type { DepartureSummary, Money, TourSummary } from "./types";

// Tur listesi filtreleri. Durum yalnızca URL'de tutulur (paylaşılabilir, yenilemede/geri-ileride korunur).
// Filtreleme, sıralama ve sayfalama sunucuda tüm katalog üzerinde yapılır; tarayıcıya yalnızca o sayfa gider.

export const DURATION_BUCKETS = [
  { value: "1-4", label: "1–4 gece", min: 1, max: 4 },
  { value: "5-7", label: "5–7 gece", min: 5, max: 7 },
  { value: "8-10", label: "8–10 gece", min: 8, max: 10 },
  { value: "11+", label: "11 gece ve üzeri", min: 11, max: Infinity },
] as const;

/** Fiyat aralıkları yalnızca EUR fiyatlı turlara uygulanır (kur bilgisi olmadan para birimleri karşılaştırılmaz). */
export const PRICE_CURRENCY = "EUR";
export const PRICE_BUCKETS = [
  { value: "0-750", label: "750 €'ya kadar", min: 0, max: 750 },
  { value: "750-1250", label: "750 – 1.250 €", min: 750, max: 1250 },
  { value: "1250-2000", label: "1.250 – 2.000 €", min: 1250, max: 2000 },
  { value: "2000+", label: "2.000 € ve üzeri", min: 2000, max: Infinity },
] as const;

export const SORT_OPTIONS = [
  { value: "tarih", label: "En yakın kalkış" },
  { value: "fiyat-artan", label: "Fiyat (artan)" },
  { value: "fiyat-azalan", label: "Fiyat (azalan)" },
  { value: "sure", label: "Süre (kısadan uzuna)" },
  { value: "ad", label: "Tur adı (A–Z)" },
] as const;
export type SortValue = (typeof SORT_OPTIONS)[number]["value"];

export type TourQuery = {
  q?: string;
  /** Tur veya kalkış kodu, yazıldığı gibi (eşleştirme: tour-code.ts → codeKey). Tam eşleşmede liste sayfası tura yönlendirir. */
  kod?: string;
  bolge?: RegionSlug;
  kategori?: string;
  aile?: FamilySlug;
  ay?: string;
  kalkis?: string;
  sure?: string;
  havayolu?: string;
  fiyat?: string;
  vizesiz?: boolean;
  sirala: SortValue;
  sayfa: number;
};

export type FilterKey = Exclude<keyof TourQuery, "sirala" | "sayfa">;
export const FILTER_KEYS: FilterKey[] = ["q", "kod", "bolge", "kategori", "aile", "ay", "kalkis", "sure", "havayolu", "fiyat", "vizesiz"];

type RawParams = Record<string, string | string[] | undefined>;

const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v)?.trim() || undefined;

/** URL parametrelerini doğrular. Geçersiz değerler atılır ve `invalid` listesinde döner. */
export function parseQuery(params: RawParams): { query: TourQuery; invalid: string[] } {
  const invalid: string[] = [];
  const query: TourQuery = { sirala: "tarih", sayfa: 1 };

  // Ana sayfa araması JavaScript olmadan "destinasyon=bolge:avrupa" / "kategori:paris-turlari" gönderir
  const dest = first(params.destinasyon);
  if (dest) {
    const [kind, value] = dest.split(":");
    if ((kind === "bolge" || kind === "kategori") && value && params[kind] === undefined) params = { ...params, [kind]: value };
  }

  const q = first(params.q);
  if (q) query.q = q.slice(0, 80);
  // Kod: yalnızca harf ve rakamlar sayılır (tire, boşluk, iki nokta yok sayılır); hiç harf/rakam yoksa geçersiz
  const kod = first(params.kod)?.replace(/\s+/g, " ");
  if (kod) {
    if (codeKey(kod)) query.kod = kod.slice(0, CODE_MAX_LENGTH);
    else invalid.push("kod");
  }

  const check = <K extends FilterKey>(key: K, ok: (v: string) => boolean, map: (v: string) => TourQuery[K]) => {
    const v = first(params[key]);
    if (v === undefined) return;
    if (ok(v)) query[key] = map(v);
    else invalid.push(key);
  };
  check("bolge", (v) => regions.some((r) => r.slug === v), (v) => v as RegionSlug);
  check("kategori", (v) => /^[a-z0-9-]{1,80}$/.test(v), (v) => v);
  check("aile", (v) => families.some((f) => f.slug === v), (v) => v as FamilySlug);
  check("ay", (v) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v), (v) => v);
  check("kalkis", (v) => /^[A-Za-z]{3}$/.test(v), (v) => v.toUpperCase());
  check("sure", (v) => DURATION_BUCKETS.some((b) => b.value === v), (v) => v);
  check("havayolu", (v) => /^[A-Za-z0-9]{2}$/.test(v), (v) => v.toUpperCase());
  check("fiyat", (v) => PRICE_BUCKETS.some((b) => b.value === v), (v) => v);
  check("vizesiz", (v) => v === "1", () => true);

  const sort = first(params.sirala);
  if (sort) {
    if (SORT_OPTIONS.some((s) => s.value === sort)) query.sirala = sort as SortValue;
    else invalid.push("sirala");
  }
  const page = first(params.sayfa);
  if (page) {
    const n = Number(page);
    if (Number.isInteger(n) && n >= 1 && n <= 1000) query.sayfa = n;
    else invalid.push("sayfa");
  }
  return { query, invalid };
}

export type ResultItem = {
  tour: TourSummary;
  /** Filtreye uyan kalkışlar (ay filtresi yoksa tümü) */
  departures: DepartureSummary[];
  fromPrice: Money | null;
};

function matchingDepartures(tour: TourSummary, query: TourQuery): DepartureSummary[] {
  return query.ay ? tour.departures.filter((d) => d.date.startsWith(query.ay!)) : tour.departures;
}

function matches(tour: TourSummary, deps: DepartureSummary[], query: TourQuery, skip?: FilterKey): boolean {
  if (deps.length === 0) return false;
  if (skip !== "q" && query.q) {
    const haystack = searchKey([tour.name, tour.sourceName ?? "", tour.code, tour.category?.name ?? "", tour.airline?.name ?? ""].join(" "));
    if (!searchKey(query.q).split(" ").every((token) => haystack.includes(token))) return false;
  }
  if (skip !== "kod" && query.kod && !matchesCode(tour.code, codeKey(query.kod))) return false;
  if (skip !== "bolge" && query.bolge && tour.region !== query.bolge) return false;
  if (skip !== "kategori" && query.kategori && tour.category?.slug !== query.kategori) return false;
  if (skip !== "aile" && query.aile && tour.family !== query.aile) return false;
  if (skip !== "kalkis" && query.kalkis && tour.departureAirport?.code !== query.kalkis) return false;
  if (skip !== "havayolu" && query.havayolu && tour.airline?.code !== query.havayolu) return false;
  if (skip !== "vizesiz" && query.vizesiz && !tour.visaFree) return false;
  if (skip !== "sure" && query.sure) {
    const bucket = DURATION_BUCKETS.find((b) => b.value === query.sure)!;
    if (tour.nights === null || tour.nights < bucket.min || tour.nights > bucket.max) return false;
  }
  if (skip !== "fiyat" && query.fiyat) {
    const bucket = PRICE_BUCKETS.find((b) => b.value === query.fiyat)!;
    const price = lowestPrice(deps.map((d) => d.price));
    if (!price || price.currency !== PRICE_CURRENCY || price.amount < bucket.min || price.amount >= bucket.max) return false;
  }
  return true;
}

/** Ay filtresi dışındaki boyutlarda kullanılan kalkışlar; ay filtresi facet sayımında atlanabilir */
function depsFor(tour: TourSummary, query: TourQuery, skip?: FilterKey) {
  return skip === "ay" ? tour.departures : matchingDepartures(tour, query);
}

function compare(a: ResultItem, b: ResultItem, sort: SortValue): number {
  const byDate = () => a.departures[0].date.localeCompare(b.departures[0].date);
  switch (sort) {
    case "fiyat-artan":
    case "fiyat-azalan": {
      // Fiyatı olmayanlar her zaman sonda; para birimleri kendi içinde sıralanır (EUR önce).
      const pa = a.fromPrice;
      const pb = b.fromPrice;
      if (!pa || !pb) return pa ? -1 : pb ? 1 : byDate();
      if (pa.currency !== pb.currency) {
        if (pa.currency === PRICE_CURRENCY) return -1;
        if (pb.currency === PRICE_CURRENCY) return 1;
        return pa.currency.localeCompare(pb.currency);
      }
      const diff = sort === "fiyat-artan" ? pa.amount - pb.amount : pb.amount - pa.amount;
      return diff || byDate();
    }
    case "sure":
      return (a.tour.nights ?? Infinity) - (b.tour.nights ?? Infinity) || byDate();
    case "ad":
      return a.tour.name.localeCompare(b.tour.name, "tr-TR");
    default:
      return byDate() || a.tour.name.localeCompare(b.tour.name, "tr-TR");
  }
}

export type FacetOption = { value: string; label: string; count: number };
export type Facets = Record<"bolge" | "kategori" | "aile" | "ay" | "kalkis" | "sure" | "havayolu" | "fiyat", FacetOption[]> & {
  vizesiz: number;
};

export type SearchResult = {
  items: ResultItem[];
  total: number;
  page: number;
  pageCount: number;
  facets: Facets;
};

/** Facet etiketleri dile göre verilir; verilmeyenler Türkçe yapılandırmadaki adlarla yazılır */
export type FacetLabels = {
  month: (ym: string) => string;
  region?: (slug: RegionSlug) => string;
  /** Ürün ailesinin görünen adı (Site Yönetimi → Ürün aileleri); yoksa yerleşik ad */
  family?: (slug: FamilySlug) => string;
  duration?: (value: string) => string;
  price?: (value: string) => string;
  airport?: (code: string, fallback: string) => string;
  airline?: (code: string, fallback: string) => string;
};

export function searchTours(tours: TourSummary[], query: TourQuery, labels: FacetLabels): SearchResult {
  const all: ResultItem[] = [];
  for (const tour of tours) {
    const deps = matchingDepartures(tour, query);
    if (matches(tour, deps, query)) all.push({ tour, departures: deps, fromPrice: lowestPrice(deps.map((d) => d.price)) });
  }
  all.sort((a, b) => compare(a, b, query.sirala));

  const pageCount = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  const page = Math.min(query.sayfa, pageCount);
  const items = all.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return { items, total: all.length, page, pageCount, facets: buildFacets(tours, query, labels) };
}

function buildFacets(tours: TourSummary[], query: TourQuery, labels: FacetLabels): Facets {
  const count = (key: FilterKey, valuesOf: (t: TourSummary, deps: DepartureSummary[]) => (string | null)[]) => {
    const counts = new Map<string, number>();
    for (const tour of tours) {
      const deps = depsFor(tour, query, key);
      if (!matches(tour, deps, query, key)) continue;
      for (const v of new Set(valuesOf(tour, deps))) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    // Seçili değer diğer filtrelerle sonuçsuz kaldıysa da (ör. tur kodu + bölge) adıyla, 0 sayısıyla listede kalsın
    const selected = query[key];
    if (typeof selected === "string" && !counts.has(selected)) counts.set(selected, 0);
    return counts;
  };
  const list = (counts: Map<string, number>, label: (v: string) => string, order?: string[]) =>
    [...counts.entries()]
      .map(([value, n]) => ({ value, label: label(value), count: n }))
      .sort((a, b) =>
        order ? order.indexOf(a.value) - order.indexOf(b.value) : a.label.localeCompare(b.label, "tr-TR"),
      );

  const categoryNames = new Map(tours.flatMap((t) => (t.category ? [[t.category.slug, t.category.name] as const] : [])));
  const airportNames = new Map(tours.flatMap((t) => (t.departureAirport ? [[t.departureAirport.code, t.departureAirport.label] as const] : [])));
  const airlineNames = new Map(tours.flatMap((t) => (t.airline ? [[t.airline.code, t.airline.name] as const] : [])));

  const visa = count("vizesiz", (t) => [t.visaFree ? "1" : null]);
  const months = count("ay", (_t, deps) => deps.map((d) => d.date.slice(0, 7)));

  return {
    bolge: list(
      count("bolge", (t) => [t.region]),
      (v) => labels.region?.(v as RegionSlug) ?? regions.find((r) => r.slug === v)!.name,
      regions.map((r) => r.slug),
    ),
    kategori: list(count("kategori", (t) => [t.category?.slug ?? null]), (v) => categoryNames.get(v) ?? v),
    aile: list(count("aile", (t) => [t.family]), (v) => labels.family?.(v as FamilySlug) ?? families.find((f) => f.slug === v)!.name, families.map((f) => f.slug)),
    ay: [...months.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([value, n]) => ({ value, label: labels.month(value), count: n })),
    kalkis: list(count("kalkis", (t) => [t.departureAirport?.code ?? null]), (v) => labels.airport?.(v, airportNames.get(v) ?? v) ?? airportNames.get(v) ?? v),
    sure: list(
      count("sure", (t) => [DURATION_BUCKETS.find((b) => t.nights !== null && t.nights >= b.min && t.nights <= b.max)?.value ?? null]),
      (v) => labels.duration?.(v) ?? DURATION_BUCKETS.find((b) => b.value === v)!.label,
      DURATION_BUCKETS.map((b) => b.value),
    ),
    havayolu: list(count("havayolu", (t) => [t.airline?.code ?? null]), (v) => labels.airline?.(v, airlineNames.get(v) ?? v) ?? airlineNames.get(v) ?? v),
    fiyat: list(
      count("fiyat", (_t, deps) => {
        const p = lowestPrice(deps.map((d) => d.price));
        if (!p || p.currency !== PRICE_CURRENCY) return [];
        return [PRICE_BUCKETS.find((b) => p.amount >= b.min && p.amount < b.max)?.value ?? null];
      }),
      (v) => labels.price?.(v) ?? PRICE_BUCKETS.find((b) => b.value === v)!.label,
      PRICE_BUCKETS.map((b) => b.value),
    ),
    vizesiz: visa.get("1") ?? 0,
  };
}

/** Sorgudan URL üretir. Varsayılanlar (sirala=tarih, sayfa=1) yazılmaz. */
export function toSearchParams(query: Partial<TourQuery>): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const v = query[key];
    if (v === undefined || v === false || v === "") continue;
    params.set(key, v === true ? "1" : String(v));
  }
  if (query.sirala && query.sirala !== "tarih") params.set("sirala", query.sirala);
  if (query.sayfa && query.sayfa > 1) params.set("sayfa", String(query.sayfa));
  return params;
}

export function hrefFor(query: Partial<TourQuery>, base = "/turlar"): string {
  const qs = toSearchParams(query).toString();
  return qs ? `${base}?${qs}` : base;
}

export function hasActiveFilters(query: TourQuery): boolean {
  return FILTER_KEYS.some((k) => query[k] !== undefined);
}
